import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/*
 * Side-panel state of the new shell (plan-ds §4.7). Web-only slice: the mobile app has its own root reducer,
 * and this file imports nothing from the DOM, the design system or the shell.
 *
 * Rules (mock §1.2): one panel at a time; the same key toggles it closed, another key swaps the content in
 * the same aside; page mode exists for `ai`, `chat` and `notif` only, survives switching between those three
 * and ends when `help` or `acc` opens or the panel closes. Width clamping ([22.5rem, 50% of the shell]) needs
 * the live shell width, so the panel host clamps before it dispatches `setPanelWidth`.
 */

export type SidePanelKey = "ai" | "chat" | "help" | "notif" | "acc";
/** Tab order = mock logic.js:176; "activity" is labelled "Notifications" (critic #7, D-G). */
export type NotifTab = "activity" | "action" | "escrow" | "support" | "exports";
export type ActFilter = "all" | "overdue" | "review" | "transmittal";

export interface AiPendingPrompt {
  text: string;
  pagePath: string | null;
}

export interface SidePanelState {
  panel: SidePanelKey | null;
  /** Side width in rem (pinned / floating). Default 26.25 (420). */
  widthRem: number;
  /** Pinned = pushes the page; false = floats over it. Default true. */
  pinned: boolean;
  pageMode: boolean;
  notifTab: NotifTab;
  actFilter: ActFilter;
  aiPendingPrompt: AiPendingPrompt | null;
}

export interface OpenPanelOptions {
  notifTab?: NotifTab;
  actFilter?: ActFilter;
}

interface OpenPanelPayload {
  key: SidePanelKey;
  opts?: OpenPanelOptions;
}

export const SIDE_PANEL_DEFAULT_WIDTH_REM = 26.25;

const PAGEABLE_PANELS: readonly SidePanelKey[] = ["ai", "chat", "notif"];

/** AI, Chat and Notifications can open as a full page (mock `PAGEABLE`). */
export function isPageablePanel(key: SidePanelKey | null): boolean {
  return key !== null && PAGEABLE_PANELS.includes(key);
}

export const SIDE_PANEL_INITIAL_STATE: SidePanelState = {
  panel: null,
  widthRem: SIDE_PANEL_DEFAULT_WIDTH_REM,
  pinned: true,
  pageMode: false,
  notifTab: "activity",
  actFilter: "all",
  aiPendingPrompt: null,
};

function open(state: SidePanelState, key: SidePanelKey, opts: OpenPanelOptions | undefined): void {
  state.panel = key;
  if (!isPageablePanel(key)) state.pageMode = false;
  if (opts?.notifTab !== undefined) state.notifTab = opts.notifTab;
  if (opts?.actFilter !== undefined) state.actFilter = opts.actFilter;
}

function close(state: SidePanelState): void {
  state.panel = null;
  state.pageMode = false;
}

const sidePanelSlice = createSlice({
  name: "sidePanel",
  initialState: SIDE_PANEL_INITIAL_STATE,
  reducers: {
    openPanel: {
      reducer: (state: SidePanelState, action: PayloadAction<OpenPanelPayload>) => {
        open(state, action.payload.key, action.payload.opts);
      },
      prepare: (key: SidePanelKey, opts?: OpenPanelOptions): { payload: OpenPanelPayload } => ({
        payload: opts === undefined ? { key } : { key, opts },
      }),
    },
    togglePanel: (state: SidePanelState, action: PayloadAction<SidePanelKey>) => {
      if (state.panel === action.payload) close(state);
      else open(state, action.payload, undefined);
    },
    closePanel: (state: SidePanelState) => {
      close(state);
    },
    setPanelWidth: (state: SidePanelState, action: PayloadAction<number>) => {
      if (Number.isFinite(action.payload) && action.payload > 0) state.widthRem = action.payload;
    },
    setPinned: (state: SidePanelState, action: PayloadAction<boolean>) => {
      state.pinned = action.payload;
    },
    enterPageMode: (state: SidePanelState) => {
      if (isPageablePanel(state.panel)) state.pageMode = true;
    },
    exitPageMode: (state: SidePanelState) => {
      state.pageMode = false;
    },
    setNotifTab: (state: SidePanelState, action: PayloadAction<NotifTab>) => {
      state.notifTab = action.payload;
    },
    setActFilter: (state: SidePanelState, action: PayloadAction<ActFilter>) => {
      state.actFilter = action.payload;
    },
    setAiPendingPrompt: (state: SidePanelState, action: PayloadAction<AiPendingPrompt | null>) => {
      state.aiPendingPrompt = action.payload;
    },
    consumeAiPendingPrompt: (state: SidePanelState) => {
      state.aiPendingPrompt = null;
    },
  },
});

export const {
  openPanel,
  togglePanel,
  closePanel,
  setPanelWidth,
  setPinned,
  enterPageMode,
  exitPageMode,
  setNotifTab,
  setActFilter,
  setAiPendingPrompt,
  consumeAiPendingPrompt,
} = sidePanelSlice.actions;

export const sidePanel = sidePanelSlice.reducer;

export default sidePanelSlice.reducer;
